import { useEffect } from "react";
import { AuthApi } from "../apis";
import { storeSession } from "../utils/auth";

// How often to renew the token while the app stays open in a tab. Well
// inside the 30-day token lifetime (see backend src/helper/token.js), so an
// open tab never approaches expiry, and reopening the app after days away
// still renews on the very next mount below.
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Keeps a signed-in session alive without asking the user to log in again.
 *
 * The backend issues a long-lived token, but "long-lived" still ends
 * eventually — this is what makes the session slide forward instead: every
 * time the app is open, it quietly asks the backend for a fresh token before
 * the old one runs out. A user who opens the app at least once within the
 * token's lifetime is renewed and never sees the login screen; only real
 * inactivity beyond that window ends the session.
 *
 * Runs once at the app root (see App.js) rather than per-screen, so it fires
 * exactly once per app load regardless of which page the user lands on.
 */
export default function useSessionRefresh() {
  useEffect(() => {
    let cancelled = false;

    const refresh = () => {
      // No token yet (logged out) — nothing to renew. AuthApi.refreshToken
      // reads the stored token itself via the shared axios interceptor, but
      // skipping the call entirely when signed out avoids a pointless
      // request (and possible silent redirect loop) on the login screen.
      if (!localStorage.getItem("token")) return;

      AuthApi.refreshToken()
        .then((res) => {
          if (cancelled) return;
          // Stores the permissions too, which is how a role edit reaches an
          // already signed-in client: the admin changes what a role may do,
          // and the next refresh — on this interval, or on the next app
          // load — hands that client its new set.
          storeSession(res?.data?.data || {});
        })
        .catch(() => {
          // A genuinely expired/invalid token already triggers logout and
          // redirect inside the shared axios interceptor (silently, since
          // this call passes silent: true). Any other failure — offline,
          // server hiccup — just means we try again on the next interval or
          // the next app load; nothing to do here.
        });
    };

    refresh();
    const id = setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
}
